<template>
  <div class="project-details">
    <div class="container">
      <div v-if="project" class="project-content">
        <div class="back-navigation">
          <RouterLink to="/projects" class="back-btn">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            {{ $t('projects.backToProjects') }}
          </RouterLink>
          
          <button class="close-btn" @click="closeProject">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        
        <h1 class="project-title">{{ project.title }}</h1>
        
        <div class="project-tags">
          <span v-for="(tag, index) in project.tags" :key="index" class="tag">{{ tag }}</span>
        </div>
        
        <div class="project-gallery">
          <img :src="projectImage" :alt="project.title" class="main-image" />
          
          <div v-if="project.gallery && project.gallery.length" class="gallery-thumbnails">
            <img 
              v-for="(image, index) in project.gallery" 
              :key="index" 
              :src="image" 
              :alt="`${project.title} - ${$t('projects.image')} ${index + 1}`" 
              class="thumbnail" 
              @click="openLightbox(index)"
            />
          </div>
        </div>
        
        <div class="project-description">
          <h2>{{ $t('projects.overview') }}</h2>
          <p>{{ project.description }}</p>
          <p v-if="project.longDescription">{{ project.longDescription }}</p>
        </div>
        
        <div class="project-features">
          <h2>{{ $t('projects.keyFeatures') }}</h2>
          <ul class="features-list">
            <li v-for="(feature, index) in project.features || defaultFeatures" :key="index" class="feature-item">
              {{ feature }}
            </li>
          </ul>
        </div>
        
        <div class="project-technologies">
          <h2>{{ $t('projects.technologiesUsed') }}</h2>
          <div class="technologies-list">
            <div v-for="(tech, index) in project.technologies || project.tags" :key="index" class="technology-item">
              {{ tech }}
            </div>
          </div>
        </div>
        
        <div v-if="project.coauthors && project.coauthors.length" class="project-coauthors">
          <h2>{{ $t('projects.coauthors') }}</h2>
          <div class="coauthors-list">
            <div v-for="(coauthor, index) in project.coauthors" :key="index" class="coauthor-card">
              <div class="coauthor-info">
                <h3 class="coauthor-name">{{ coauthor.name }}</h3>
                <div class="coauthor-links">
                  <a :href="coauthor.github" target="_blank" rel="noopener noreferrer" class="coauthor-link github-link">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"></path>
                    </svg>
                    GitHub
                  </a>
                  <a :href="coauthor.linkedin" target="_blank" rel="noopener noreferrer" class="coauthor-link linkedin-link">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                      <rect x="2" y="9" width="4" height="12"></rect>
                      <circle cx="4" cy="4" r="2"></circle>
                    </svg>
                    LinkedIn
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="project-links">
          <a v-if="project.liveUrl" :href="project.liveUrl" target="_blank" rel="noopener noreferrer" class="btn btn-primary">
            {{ $t('projects.viewLive') }}
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
              <polyline points="15 3 21 3 21 9"></polyline>
              <line x1="10" y1="14" x2="21" y2="3"></line>
            </svg>
          </a>
          
          <a v-if="project.codeUrl" :href="project.codeUrl" target="_blank" rel="noopener noreferrer" class="btn btn-outline">
            {{ $t('projects.viewCode') }}
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"></path>
            </svg>
          </a>
        </div>
      </div>
      
      <div v-else class="not-found">
        <h2>{{ $t('projects.notFound') }}</h2>
        <p>{{ $t('projects.notFoundDescription') }}</p>
        <RouterLink to="/projects" class="btn btn-primary">{{ $t('projects.backToProjects') }}</RouterLink>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { RouterLink } from 'vue-router'
import { useProjectsStore } from '@/stores/projects'

export default defineComponent({
  name: 'ProjectDetailsView',
  components: {
    RouterLink
  },
  props: {
    id: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      defaultFeatures: [
        'Responsive design for all devices',
        'Intuitive user interface',
        'Performance optimization',
        'Cross-browser compatibility',
        'Accessibility considerations'
      ]
    }
  },
  computed: {
    project() {
      const projectsStore = useProjectsStore()
      return projectsStore.getProjectById(this.id)
    },
    projectImage() {
      if (!this.project) return null;
      return this.project.image || this.getDefaultProjectImage();
    }
  },
  methods: {
    openLightbox(index) {
      // Lightbox functionality would be implemented here
      console.log('Opening lightbox for image at index:', index)
    },
    closeProject() {
      this.$router.push('/projects');
    },
    getDefaultProjectImage() {
      // Use the first project image from projects folder as default fallback
      return '/images/projects/llm-chess-website.png';
    }
  }
})
</script>

<style lang="scss" scoped>
.project-details {
  padding: 2rem 0;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: var(--color-background);
  z-index: 1000;
  overflow-y: auto;
}

.project-content {
  position: relative;
  z-index: 1;
}

.back-navigation {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--color-text);
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  background-color: var(--color-card-bg);
  
  &:hover {
    background-color: rgba(var(--color-primary-rgb, 248, 198, 48), 0.1);
    color: var(--color-primary);
    transform: rotate(90deg);
  }
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--color-text);
  transition: all 0.3s ease;
  
  &:hover {
    color: var(--color-primary);
    
    svg {
      transform: translateX(-5px);
    }
  }
  
  svg {
    transition: transform 0.3s ease;
  }
}

.project-title {
  font-size: 2.5rem;
  margin-bottom: 1rem;
}

.project-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 2rem;
}

.tag {
  background-color: rgba(var(--color-primary-rgb, 248, 198, 48), 0.1);
  color: var(--color-primary);
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
}

.project-gallery {
  margin-bottom: 2.5rem;
  
  .main-image {
    width: 100%;
    height: auto;
    border-radius: 8px;
    margin-bottom: 1rem;
    box-shadow: 0 4px 10px var(--color-shadow);
  }
  
  .gallery-thumbnails {
    display: flex;
    gap: 1rem;
    overflow-x: auto;
    padding-bottom: 0.5rem;
    
    .thumbnail {
      width: 100px;
      height: 100px;
      object-fit: cover;
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.3s ease;
      
      &:hover {
        transform: scale(1.05);
        box-shadow: 0 2px 5px var(--color-shadow);
      }
    }
  }
}

.project-features,
.project-technologies,
.project-description {
  margin-bottom: 2.5rem;
  background-color: var(--color-card-bg);
  border-radius: 12px;
  padding: 1.5rem;
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 20px var(--color-shadow);
  
  h2 {
    margin-bottom: 1rem;
    position: relative;
    
    &::after {
      content: '';
      display: block;
      width: 40px;
      height: 3px;
      background-color: var(--color-primary);
      margin-top: 0.5rem;
    }
  }
  
  p {
    margin-bottom: 1rem;
    line-height: 1.7;
  }
}

.features-list {
  list-style: none;
  padding: 0;
}

.feature-item {
  padding: 0.5rem 0;
  position: relative;
  padding-left: 1.5rem;
  
  &::before {
    content: '✓';
    color: var(--color-primary);
    position: absolute;
    left: 0;
    font-weight: bold;
  }
}

.technologies-list {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.technology-item {
  background-color: var(--color-bg-secondary);
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.875rem;
}

.project-coauthors {
  margin-bottom: 2rem;
}

.coauthors-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
}

.coauthor-card {
  background-color: var(--color-bg-secondary);
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px var(--color-shadow);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 10px var(--color-shadow);
  }
}

.coauthor-info {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.coauthor-name {
  font-size: 1.1rem;
  margin: 0;
  color: var(--color-heading);
}

.coauthor-links {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.coauthor-link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.875rem;
  transition: all 0.3s ease;
  
  &.github-link {
    background-color: #24292e;
    color: white;
    
    &:hover {
      background-color: #2c3136;
    }
  }
  
  &.linkedin-link {
    background-color: #0077b5;
    color: white;
    
    &:hover {
      background-color: #0069a1;
    }
  }
}

.project-links {
  display: flex;
  gap: 1rem;
  
  a {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    svg {
      transition: transform 0.3s ease;
    }
    
    &:hover svg {
      transform: translateX(3px);
    }
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
}

.not-found {
  text-align: center;
  padding: 4rem 0;
  
  h2 {
    margin-bottom: 1rem;
  }
  
  p {
    margin-bottom: 2rem;
    opacity: 0.8;
  }
}

@media (max-width: 768px) {
  .project-title {
    font-size: 2rem;
  }
}
</style>